import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  styled
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner";
import { CommunicationOptions } from "../../models/employers/communicationOptions";
import { useConfig } from "../../utils/useConfig";
import { useData } from "../../utils/useData";

const OptionsContainer = styled(Box)({
  padding: 24,
  borderRadius: 12,
  marginTop: 24
});

const OptionsTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: 16,
  marginBottom: 16
});

const SubmitButtonContainer = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 24
});

const CommunicationSettingsForm = () => {
  const { employer } = useData();
  const { config } = useConfig();
  const { getToken } = useKindeAuth();
  const [initialFormValues, setInitialFormValues] =
    useState<CommunicationOptions>({
      receiveAccountBalanceEmails: false
    });

  useEffect(() => {
    if (employer.data) {
      setInitialFormValues({
        receiveAccountBalanceEmails:
          employer.data.communicationOptions?.receiveAccountBalanceEmails ||
          false
      });
    }
  }, [employer.data]);

  const updateCommunicationOptionsMutation = useMutation(
    async (communicationOptions: CommunicationOptions) => {
      const token = await getToken();
      await fetch(
        `${config?.API_URL}/employers/${employer.data?.id}/communication-options`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(communicationOptions)
        }
      );

      return { success: true };
    },
    {
      onSuccess: () => {
        toast.success("Communication preferences updated successfully");
        employer.refetch();
      },

      onError: (error) => {
        console.error("Error updating communication preferences:", error);
        toast.error("Failed to update communication preferences");
      }
    }
  );

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await updateCommunicationOptionsMutation.mutateAsync(values);
        setSubmitting(false);
      } catch (error) {
        console.error("Error saving communication preferences:", error);
        setSubmitting(false);
      }
    }
  });

  const employerIsLoading = employer.isIdle || employer.isLoading;

  if (employerIsLoading) return <LoadingSpinner />;

  return (
    <form onSubmit={formik.handleSubmit}>
      <OptionsContainer>
        <OptionsTitle>Communication Preferences</OptionsTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                name="receiveAccountBalanceEmails"
                checked={formik.values.receiveAccountBalanceEmails}
                onChange={formik.handleChange}
                color="primary"
              />
            }
            label="Receive monthly balance emails"
          />
        </Box>
        <SubmitButtonContainer>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={formik.isSubmitting}
            disabled={!formik.dirty}
          >
            Save Changes
          </LoadingButton>
        </SubmitButtonContainer>
      </OptionsContainer>
    </form>
  );
};

export { CommunicationSettingsForm };
